import { TimeRange, Timestamp, toTimestamp } from '@mi-front/core';
import moment from 'moment-timezone';
import { FragmentChunk } from '../../fragments/types';

export class ChunksRangeConverter {
  get FROM(): Timestamp {
    return moment.utc('2010-01-01T00:00:00Z').local(true).valueOf() as Timestamp;
  }

  // base chunks
  private realChunks: {
    from: Timestamp;
    to: Timestamp;
    chunk: FragmentChunk;
  }[] = [];

  // relative to scale based on order
  private relativeChunks: {
    from: Timestamp;
    to: Timestamp;
    chunk: FragmentChunk;
  }[] = [];

  constructor(chunks: readonly FragmentChunk[] = []) {
    this.setChunks(chunks);
  }

  setChunks(chunks: readonly FragmentChunk[]) {
    this.realChunks = chunks.map((chunk) => {
      return {
        from: toTimestamp(chunk.from),
        to: toTimestamp(chunk.to),
        chunk,
      };
    });

    let fromAccum = this.FROM;

    this.relativeChunks = chunks.map((chunk) => {
      const to = (fromAccum + chunk.duration) as Timestamp;

      const c = {
        from: fromAccum,
        to,
        chunk,
      };

      fromAccum = to;

      return c;
    });
  }

  private validate() {
    if (!this.realChunks.length) {
      throw new Error('No chunks!');
    }
  }

  // get the relative range for an editable fragment
  getRelativeRange(): TimeRange<Timestamp> {
    this.validate();

    return {
      from: this.relativeChunks[0].from,
      to: this.relativeChunks.slice(-1)[0].to,
    };
  }

  // converts a relative ts (related to range) to a chunk
  getChunkForRealTs(relativeTs: Timestamp): FragmentChunk | null {
    this.validate();

    const rc = this.realChunks.find((c) => c.from <= relativeTs && c.to > relativeTs);

    return rc?.chunk || null;
  }

  // converts a relative ts (related to range) to a chunk
  getChunkForRelativeTs(relativeTs: Timestamp): FragmentChunk | null {
    this.validate();

    const rc = this.relativeChunks.find((c) => c.from <= relativeTs && c.to > relativeTs);

    return rc?.chunk || null;
  }

  // converts a relative ts (related to range) to chunk ts
  toRealTs(relativeTs: Timestamp): Timestamp | null {
    this.validate();

    const rc = this.relativeChunks.find((c) => c.from <= relativeTs && c.to > relativeTs);

    if (!rc) {
      return null;
    }

    // calculate the difference from relative start and add it to the
    // real chunk start
    return (relativeTs - rc.from + toTimestamp(rc.chunk.from)) as Timestamp;
  }

  // converts a real chunk ts to a relative one (related to range)
  toRelativeTs(realTs: Timestamp): Timestamp | null {
    this.validate();

    const rc = this.realChunks.find((c) => c.from <= realTs && c.to > realTs);

    if (!rc) {
      return null;
    }

    const relativeEntry = this.relativeChunks.find((c) => c.chunk === rc.chunk)!;

    // calculate the difference from relative start and add it to the
    // real chunk start
    return (relativeEntry.from + (realTs - rc.from)) as Timestamp;
  }
}
